/**
 * AcademiaAIPanel — Right-side AI advisory panel for the University Home dashboard
 *
 * NAV-X5: Read-only institutional Q&A surface.
 * All prompts route to /ai with the prompt pre-seeded.
 * No direct DB access. No write actions without ARGOV.
 *
 * Governance:
 *  - AI Advisory · ARGOV Active
 *  - Authority: ADVISORY (recommendations only)
 *  - Write actions require human approval via ARGOV chain
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";

const PROMPTS = [
  {
    id: "intervention",
    label: "Which students need intervention?",
    icon: AlertCircle,
    iconClass: "text-amber-500",
  },
  {
    id: "performance",
    label: "Summarize institutional performance.",
    icon: Sparkles,
    iconClass: "text-blue-500",
  },
  {
    id: "courses",
    label: "Which courses show declining outcomes?",
    icon: ChevronDown,
    iconClass: "text-red-400",
  },
  {
    id: "finance",
    label: "Explain current finance exceptions.",
    icon: AlertCircle,
    iconClass: "text-amber-500",
  },
  {
    id: "approvals",
    label: "Show actions awaiting approval.",
    icon: ShieldCheck,
    iconClass: "text-purple-500",
  },
  {
    id: "evidence",
    label: "Summarize governance evidence.",
    icon: ShieldCheck,
    iconClass: "text-emerald-500",
  },
];

export function AcademiaAIPanel({ collapsed: externalCollapsed, onToggle }) {
  const navigate = useNavigate();
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = externalCollapsed !== undefined ? externalCollapsed : localCollapsed;
  const toggle = onToggle || (() => setLocalCollapsed((v) => !v));

  const handlePrompt = (prompt) => {
    // Route to /ai with the prompt pre-seeded via state
    navigate("/ai", { state: { seedPrompt: prompt.label } });
  };

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden flex flex-col"
      data-testid="academia-ai-panel"
    >
      {/* Header */}
      <button
        onClick={toggle}
        className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-muted/40 transition-colors w-full text-left"
        data-testid="academia-ai-panel-toggle"
      >
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground leading-tight">Ask Academia AI</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
              AI Advisory · ARGOV Active
            </div>
          </div>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Prompts */}
      {!collapsed && (
        <div className="px-4 pb-4 flex flex-col gap-1.5">
          {PROMPTS.map((prompt) => (
            <button
              key={prompt.id}
              onClick={() => handlePrompt(prompt)}
              data-testid={`ai-prompt-${prompt.id}`}
              className="group flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm text-foreground/70 hover:bg-primary/5 hover:text-foreground border border-transparent hover:border-border/60 transition-all"
            >
              <prompt.icon className={`h-3.5 w-3.5 shrink-0 ${prompt.iconClass}`} strokeWidth={1.75} />
              <span className="flex-1 text-xs leading-snug">{prompt.label}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all opacity-0 group-hover:opacity-100" />
            </button>
          ))}

          {/* Governance footer */}
          <div className="mt-2 pt-2 border-t border-border/50 flex items-start gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              AI responses are advisory only. Actions that affect institutional data require
              ARGOV review and human approval before execution.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
